// RC18.2 — Enterprise Design System: design tokens.
// Single source of truth for color, type, spacing, radius, and elevation. Nothing in
// `theme/index.js` or any component should hardcode a hex value that belongs here —
// per-page hardcoded status-color maps (found across CandidateProfile, Dashboard, etc. in
// the RC18.2 Phase 1 audit) are the debt this file exists to retire, page by page, over
// this and follow-up UX sprints.

// ── Brand scale — a deeper, less-generic enterprise blue than MUI's stock #1976d2,
// built as a full 50-900 tonal ramp so components can reach for a shade, not just one dot. ──
const brand = {
  50: '#EEF3FC',
  100: '#D6E2F7',
  200: '#AEC5F0',
  300: '#7FA1E6',
  400: '#4C79D6',
  500: '#2B57C4', // primary.main
  600: '#1F45A8',
  700: '#18378A',
  800: '#132B6C',
  900: '#0E2050',
};

// ── Neutral scale — a slate with a faint cool bias toward `brand`, not a flat grey. ──
const neutral = {
  0: '#FFFFFF',
  50: '#F7F8FA',
  100: '#EEF0F4',
  200: '#DFE3EA',
  300: '#C7CDD9',
  400: '#A2AABC',
  500: '#7C8598',
  600: '#5C6478',
  700: '#434A5C',
  800: '#2B3040',
  900: '#181B26',
  950: '#0E1017',
};

// ── Semantic — status meaning, deliberately not the brand hue, so "in progress" (brand)
// never gets confused with "success"/"warning"/"critical". ──
const semantic = {
  success: { 50: '#E9F7EF', 400: '#3FB667', 500: '#2E9950', 700: '#1E7038' },
  warning: { 50: '#FFF6E6', 400: '#E8A83C', 500: '#C98A1F', 700: '#8F5F0F' },
  error: { 50: '#FCEBEC', 400: '#E15361', 500: '#C93244', 700: '#932232' },
  info: { 50: '#EAF3FC', 400: '#4C9BE0', 500: '#2E7FC4', 700: '#1F5C90' },
};

// ── Stage/category accents — a small fixed palette for pipeline stages, note categories,
// and similar "many small labeled buckets" UI, kept apart from brand/semantic so it can
// rotate independently. ──
const categorical = ['#2B57C4', '#3FA1B6', '#3FB667', '#E8A83C', '#C9587A', '#7A5FC9', '#4C79D6', '#8F8F3C'];

const spacing = 8; // MUI spacing unit (px) — every gap/padding should be a multiple of this.

const radius = {
  sm: 6,
  md: 10,
  lg: 16,
  pill: 999,
};

const elevation = {
  // Soft, layered shadows rather than MUI's default harsh drop-shadow — tuned for a light
  // surface; theme/index.js supplies a separate, subtler set for dark mode.
  1: '0 1px 2px rgba(14,16,23,0.06), 0 1px 1px rgba(14,16,23,0.04)',
  2: '0 2px 8px rgba(14,16,23,0.08), 0 1px 2px rgba(14,16,23,0.04)',
  3: '0 8px 24px rgba(14,16,23,0.10), 0 2px 6px rgba(14,16,23,0.05)',
  4: '0 16px 40px rgba(14,16,23,0.14), 0 4px 10px rgba(14,16,23,0.06)',
};

const typography = {
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMono: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
  // A compact, enterprise-density type scale (not marketing-site sized) — every heading
  // component in the design system pulls from here rather than an ad hoc variant.
  scale: {
    xs: 12,
    sm: 13,
    body: 14,
    md: 15,
    lg: 18,
    xl: 22,
    xxl: 28,
    display: 34,
  },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
};

export { brand, neutral, semantic, categorical, spacing, radius, elevation, typography };
